import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { useFragment } from "react-relay";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import {
  PlusIcon,
  PersonFilledIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  HStack,
  VStack,
  Text,
  Box,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";

import { EditBookingModal } from "../../../EditBooking/EditBookingModal";
import { bookingSummaryAnalyticsDataAtom } from "../../atoms/analytics/bookingSummaryAnalyticsDataAtom";
import { getBookingPlayerNoun } from "../../utils/getBookingPlayerNoun";
import { AddMorePlayersButton_Booking$key } from "./__generated__/AddMorePlayersButton_Booking.graphql";
import { useGetIsBookingFull } from "./hooks/useGetIsBookingFull";

type Props = {
  bookingRef: AddMorePlayersButton_Booking$key | null;
  currentSessionMemberShipTypeId: string;
  currentSessionUserClientId: string;
};

export const AddMorePlayersButton = ({
  bookingRef,
  currentSessionMemberShipTypeId,
  currentSessionUserClientId,
}: Props) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const booking = useFragment(AddMorePlayersButtonFragmentString, bookingRef);

  const isBookingFull = useGetIsBookingFull({
    bookingFragmentRef: booking ?? null,
  });

  const isAlreadyPaid =
    !!(booking?.paymentStatus === "PAID") ||
    !!booking?.rounds?.edges?.some(
      (edge) => edge?.node?.paymentStatus === "PAID"
    );

  const facility = booking?.startSlot?.agenda?.facility;
  const isPrivate = facility?.club?.supportsPrivate;
  const playerNoun = getBookingPlayerNoun({
    bookingType: booking?.__typename,
    copyOverrides: facility?.copyOverrides,
    lowercaseFirstLetter: true,
  });

  const analyticsData = useAtomValue(bookingSummaryAnalyticsDataAtom);
  const trackEvent = useTrackEvent();

  const shouldShowAddMorePlayersButton =
    !isBookingFull && !isAlreadyPaid && booking?.canUpdate;

  return shouldShowAddMorePlayersButton ? (
    <VStack paddingVertical={2} testID="add-more-players-button-container">
      <HStack justifyContent="space-between" alignItems="center">
        <HStack gap={3} alignItems="center">
          <Box
            backgroundColor="surface-secondary"
            height={24}
            width={24}
            justifyContent="center"
            alignItems="center"
            borderRadius="2"
          >
            <PersonFilledIcon />
          </Box>
          <Text>{`${t`Add more`} ${playerNoun.plural}`}</Text>
        </HStack>
        <Box
          backgroundColor="surface-secondary"
          height={32}
          width={32}
          justifyContent="center"
          alignItems="center"
          borderRadius="2"
        >
          <Pressable
            onPress={() => {
              setIsEditOpen(true);
              trackEvent(
                "BookingDetail.AddMorePlayersClicked",
                analyticsData ?? {}
              );
            }}
            testID="add-more-players-button"
          >
            <PlusIcon />
          </Pressable>
        </Box>
      </HStack>
      {booking ? (
        <EditBookingModal
          isOpen={isEditOpen}
          onClose={() => {
            setIsEditOpen(false);
          }}
          initialStep={
            isPrivate ? { type: "AddPlayerPrivate" } : { type: "AddPlayer" }
          }
          bookingRef={booking}
          currentSessionMemberShipTypeId={currentSessionMemberShipTypeId}
          currentSessionUserClientId={currentSessionUserClientId}
        />
      ) : null}
    </VStack>
  ) : null;
};

const AddMorePlayersButtonFragmentString = graphql`
  fragment AddMorePlayersButton_Booking on Booking {
    __typename
    canUpdate
    ...useGetIsBookingFull_Booking
    ...EditBookingModal_Booking
    ... on StandardBooking {
      paymentStatus
    }
    startSlot {
      agenda {
        facility {
          copyOverrides {
            lessonPlayer {
              plural
            }
            bookingPlayer {
              plural
            }
          }
          club {
            supportsPrivate
          }
        }
      }
    }
    rounds {
      edges {
        node {
          paymentStatus
        }
      }
    }
  }
`;
